import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import { format, isSameDay, addDays } from "date-fns";
import { enUS, es, fr, it, nl, de } from "date-fns/locale";
import { CustomPlaceholder, MessageBox } from "reusableComponents";
import "./styles.scss";

const localeMap = {
  en: enUS,
  es: es,
  fr: fr,
  it: it,
  nl: nl,
  de: de,
};

const CustomDatePicker = ({
  error = null,
  onDateChange,
  selectedDate,
  minDate = addDays(new Date(), 1), // Set minDate to one day after today
  className,
  styled = false,
  label,
  labelAbove = true,
  grayLabel = false,
  required,
  workingHours = {
    0: [{ from: "08:00", to: "17:00" }], // Sunday: No working hours
    1: [{ from: "08:00", to: "17:00" }], // Monday: 8:00 - 17:00
    2: [{ from: "09:00", to: "12:00" }], // Tuesday: 9:00 - 12:00
    3: [{ from: "08:30", to: "12:00" }], // Wednesday: 8:30 - 12:00
    4: [{ from: "08:00", to: "18:00" }], // Thursday: 8:00 - 18:00
    5: [{ from: "10:00", to: "14:00" }], // Friday: 10:00 - 14:00
    6: [{ from: "10:00", to: "14:00" }], // Saturday: No working hours
  }, // This is the updated prop: working hours instead of working days
}) => {
  const { t, i18n } = useTranslation("global");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const datePickerRef = useRef(null);

  const handleChange = (date) => {
    if (date && selectedDate && isSameDay(date, selectedDate)) {
      onDateChange(null);
    } else {
      onDateChange(format(date, "yyyy-MM-dd"));
    }
    setIsDatePickerOpen(false);
  };

  const handlePlaceholderClick = (event) => {
    event.preventDefault();
    setIsDatePickerOpen((prev) => !prev);
  };

  const handleClickOutside = (event) => {
    if (
      datePickerRef.current &&
      !datePickerRef.current.contains(event.target)
    ) {
      setIsDatePickerOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filterWorkingDays = (date) => {
    const day = date.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
    // Map Sunday (0) to 7 for ISO compatibility
    // const isoDay = day === 0 ? 7 : day;
    const isoDay = day;
    // Check if there are working hours for the day
    const hoursForDay = workingHours[isoDay];
    if (!hoursForDay || hoursForDay.length === 0) {
      return false; // If no working hours for the day, return false (disable the day)
    }

    // Optional: If you want to filter by specific working hours, you can use more complex logic here.
    // For now, this checks if the day has working hours.
    return true;
  };

  return (
    <div
      className={`datePickerLabel ${grayLabel ? "grayLabel" : ""} ${
        labelAbove ? "labelAbove" : "labelBelow"
      }`}
    >
      {label && (
        <p className={`datePickerLabel ${grayLabel ? "grayLabel" : ""}`}>
          {label}
          {required && <span className="required">*</span>}
        </p>
      )}{" "}
      {/* Render label if provided */}
      <div
        className={`datePickerContainer ${
          isDatePickerOpen ? "datePickerOpen" : ""
        } ${styled ? "styledDatePicker" : ""}`}
        ref={datePickerRef}
      >
        <CustomPlaceholder
          onClick={handlePlaceholderClick}
          condition={selectedDate}
          text={selectedDate && format(new Date(selectedDate), "dd/MM/yy")}
          defaultText="d/mm/yy"
        />

        <DatePicker
          selected={selectedDate}
          onChange={handleChange}
          dateFormat="P"
          className="datePicker"
          popperClassName="datePickerPopper"
          open={isDatePickerOpen}
          minDate={minDate}
          locale={localeMap[i18n.language]}
          filterDate={filterWorkingDays} // Use working hours filter
          onCalendarOpen={() => setIsDatePickerOpen(true)}
          onCalendarClose={() => setIsDatePickerOpen(false)}
        />
      </div>
      {error && <MessageBox type="error" message = {error}/>}
    </div>
  );
};

export default CustomDatePicker;
